import { useEffect, useState } from 'react';
import { api } from '../../api/client.js';
import { Loader, Alert, Empty, Toast, formatDate, relativeTime } from '../../components/ui.jsx';
import CubMascot from '../../components/CubMascot.jsx';

const rupees = (paise) => `₹${(paise / 100).toFixed(2)}`;

const STATUS = {
  pending: { cls: 'pill-warn', label: '⏳ Waiting for you' },
  paid: { cls: 'pill-good', label: '✓ Approved' },
  created: { cls: '', label: 'Started, not finished' },
  failed: { cls: 'pill-bad', label: '✗ Failed' },
  rejected: { cls: 'pill-bad', label: '✗ Rejected' },
};

export default function SuperPayments() {
  const [payments, setPayments] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = () =>
    api
      .adminPayments()
      .then(({ payments: list }) => setPayments(list))
      .catch((err) => setError(err.message));

  useEffect(() => {
    load();
  }, []);

  const approve = async (p) => {
    setBusyId(p.id);
    try {
      await api.approvePayment(p.id);
      setToast(`${p.fullName} now has access`);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (p) => {
    const reason = window.prompt(
      `Reject the payment claim from ${p.fullName} (ref ${p.upi_reference})?\n\nOptional reason:`,
      'Could not find this payment in the Razorpay dashboard',
    );
    if (reason === null) return;
    setBusyId(p.id);
    try {
      await api.rejectPayment(p.id, { reason });
      setToast(`Rejected the claim from ${p.fullName}`);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  if (error && !payments) return <Alert kind="bad">{error}</Alert>;
  if (!payments) return <Loader label="Loading payments…" />;

  const pending = payments.filter((p) => p.status === 'pending');

  return (
    <>
      <div className="page-head">
        <div>
          <h1>💳 Payments</h1>
          <p>
            Card and UPI payments made through Razorpay Checkout verify themselves. Payments made
            by scanning the QR code have no callback, so you confirm those here against your
            Razorpay dashboard.
          </p>
        </div>
        <button type="button" className="btn btn-ghost" onClick={load}>🔄 Refresh</button>
      </div>

      {error && <Alert kind="bad" onDismiss={() => setError('')}>{error}</Alert>}

      {pending.length > 0 && (
        <Alert kind="warn">
          <strong>{pending.length} QR payment{pending.length === 1 ? '' : 's'} waiting.</strong>{' '}
          Check each reference in your Razorpay dashboard before approving — approving grants 30
          days of access.
        </Alert>
      )}

      {payments.length === 0 ? (
        <Empty icon="💳" title="No payments yet" text="Payments appear here as students make them." />
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Method</th>
                <th>Reference</th>
                <th className="num">Amount</th>
                <th>Status</th>
                <th>When</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => {
                const s = STATUS[p.status] ?? { cls: '', label: p.status };
                return (
                  <tr key={p.id}>
                    <td>
                      <div className="cell-user">
                        <CubMascot avatar={p.avatar} size={34} />
                        <div>
                          <strong>{p.fullName}</strong>
                          <span className="faint small">{p.userLogin}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="pill">
                        {p.method === 'qr' ? '📱 QR' : '💳 Checkout'}
                      </span>
                    </td>
                    <td className="small">
                      <code className="code-chip">
                        {p.upi_reference || p.razorpay_payment_id || '—'}
                      </code>
                    </td>
                    <td className="num">{rupees(p.amount_paise)}</td>
                    <td>
                      <span className={`pill ${s.cls}`}>{s.label}</span>
                      {p.status === 'paid' && p.period_end && (
                        <span className="faint small" style={{ display: 'block' }}>
                          until {formatDate(p.period_end)}
                        </span>
                      )}
                      {p.reviewedBy && (
                        <span className="faint small" style={{ display: 'block' }}>
                          by {p.reviewedBy}
                        </span>
                      )}
                    </td>
                    <td className="small muted">{relativeTime(p.created_at)}</td>
                    <td>
                      {p.status === 'pending' ? (
                        <div className="row gap-xs wrap">
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => approve(p)}
                            disabled={busyId === p.id}
                          >
                            ✓ Approve
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-ghost danger"
                            onClick={() => reject(p)}
                            disabled={busyId === p.id}
                          >
                            Reject
                          </button>
                        </div>
                      ) : (
                        <span className="faint small">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Toast message={toast} onDone={() => setToast('')} />
    </>
  );
}
